import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionFuturesProgressStep, useProgress } from '@/context/progress';
import { type InstitutionFuturesReporting, institutionFuturesReportingEmirSchema, institutionFuturesReportingGeneralSchema } from '@/features/institution/futures/reporting/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';
import type { FormStep } from '@/types/components';
import { InstitutionFuturesReportingFieldsetEmir, InstitutionFuturesReportingFieldsetEmirLegend } from './fieldsets/emir';
import { InstitutionFuturesReportingFieldsetGeneral, InstitutionFuturesReportingFieldsetGeneralLegend } from './fieldsets/general';

const FORM_STEPS = {
  GENERAL: {
    Component: InstitutionFuturesReportingFieldsetGeneral,
    Legend: InstitutionFuturesReportingFieldsetGeneralLegend,
    schema: institutionFuturesReportingGeneralSchema,
  },
  EMIR: {
    Component: InstitutionFuturesReportingFieldsetEmir,
    Legend: InstitutionFuturesReportingFieldsetEmirLegend,
    schema: institutionFuturesReportingEmirSchema,
  },
} satisfies Record<string, FormStep<InstitutionFuturesReporting>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionFuturesReportingFormCurrentStep = () => {
  const { setCurrentStep } = useProgress();
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const Step = STEPS[currentStep];

  useEffect(() => {
    return setCurrentStep(
      InstitutionFuturesProgressStep.INSTITUTION_INFORMATION_FUTURES,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.FUTURES, 'reporting', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionFuturesReportingFormCurrentStep.displayName = 'InstitutionFuturesReportingFormCurrentStep';

const InstitutionFuturesReportingFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionFuturesReportingFormCurrentStepLegend.displayName = 'InstitutionFuturesReportingFormCurrentStepLegend';

const InstitutionFuturesReportingFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const navigate = useNavigate();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate('/institution/futures/client-categorisation', { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, navigate, prevStep]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionFuturesReportingFormBackButton.displayName = 'InstitutionFuturesReportingFormBackButton';

const InstitutionFuturesReportingFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.next')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionFuturesReportingFormFooter.displayName = 'InstitutionFuturesReportingFormFooter';

const InstitutionFuturesReportingForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.FUTURES, 'reporting');
  const { isPending, mutateAsync } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionFuturesReporting) => {
    await mutateAsync(
      { reporting: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/institution/futures/client-categorisation/treatment', { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.reporting ?? {};

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionFuturesReportingFormCurrentStepLegend />
        <InstitutionFuturesReportingFormBackButton />
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionFuturesReportingFormCurrentStep />
          <InstitutionFuturesReportingFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionFuturesReportingForm.displayName = 'InstitutionFuturesReportingForm';

export default InstitutionFuturesReportingForm;
